/**
 * WYSZUKIWARKA I UKŁAD OKIEN w modalu „Utwórz nowe zgłoszenie".
 *
 * [polecenie właściciela 2026-09-19] „w sekcji okna modal «Utworz nowe zgloszenie» zrob wyszukiwarke
 * dla zgloszen oraz zrob opcje filtrowania zaleznie od instniejacych modulow […] i dynamicznie
 * przestawiaj okienka".
 *
 * DLACZEGO OSOBNY PLIK, a nie kolejne `useMemo` w `MyNewTicketModal.js` (2400 linii, wspólny dla
 * kilku równoległych zadań): regułę wyszukiwania da się tu zmierzyć BEZ montowania okna — a to jest
 * cała różnica między zapadką na zachowanie a zapadką na napis w źródle. Drugi powód jest praktyczny:
 * trzech agentów pisze jednocześnie w tamtym pliku, więc im mniej tam logiki, tym mniej zderzeń.
 *
 * CO WCHODZI DO SIANA (haystack) JEDNEGO OKNA:
 *   1. nazwa okna,
 *   2. opis kafelka,
 *   3. nazwy WSZYSTKICH kategorii pod oknem (te „dane dodatkowe" spod kafelka) — człowiek szuka
 *      „monitor", a to bywa nazwa kategorii, nie okna,
 *   4. frazy słownika reguł przypisań wskazujące okno albo którąkolwiek kategorię pod nim.
 *
 * Punkt 4 jest tym, po co wyszukiwarka w ogóle powstała: „nie drukuje" to nie jest ani nazwa okna,
 * ani nazwa kategorii — to fraza ze słownika. Bez niej szukanie po ~40 oknach znajduje wyłącznie to,
 * co i tak widać na ekranie.
 */

/* Litery, których rozkład NFD nie rusza (ł/Ł nie ma znaku diakrytycznego — to osobny znak Unicode),
   więc bez tej mapy „lacze" nie znalazłoby „łącze". */
const LITERY_SPECJALNE = { ł: "l", Ł: "L", đ: "d", Đ: "D" };

/**
 * Napis do porównywania: małe litery, bez ogonków, bez zbędnych odstępów.
 * Bez tego człowiek piszący bez polskich znaków („nie dziala drukarka") nie trafiłby w nic.
 */
export function normalizuj(tekst) {
    if (tekst === null || tekst === undefined) return "";
    return String(tekst)
        .replace(/[\u0142\u0141\u0111\u0110]/g, (z) => LITERY_SPECJALNE[z] || z)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
}

/** Identyfikatory kategorii bywają GUID-ami (napis) i liczbami — porównujemy jednym kształtem. */
const klucz = (id) => (id === null || id === undefined ? "" : String(id));

/**
 * Najbliższy przodek danej kategorii spełniający warunek (np. „jest oknem", „jest grupą").
 * Zwraca `null`, gdy takiego przodka nie ma. Pętla ma licznik kroków, bo dane produkcyjne
 * potrafią mieć cykl w `parentCategoryId` (ręczne poprawki w bazie) — a cykl bez licznika
 * to zawieszona przeglądarka, nie błąd na ekranie.
 */
function przodekSpelniajacy(kategoria, poId, warunek) {
    let biezaca = kategoria;
    let kroki = 0;
    while (biezaca && kroki < 50) {
        if (warunek(biezaca)) return biezaca;
        const rodzic = poId.get(klucz(biezaca.parentCategoryId));
        if (!rodzic || rodzic === biezaca) return null;
        biezaca = rodzic;
        kroki += 1;
    }
    return null;
}

const jestOknem = (c) => !!c && !!c.isTile && !c.isGroup;
const jestGrupa = (c) => !!c && !!c.isGroup;

/**
 * Indeks wyszukiwania: `Map(idOkna -> znormalizowane siano)`.
 *
 * @param kategorie    pełna lista kategorii z API (okna, grupy, kategorie pod oknami)
 * @param frazyRegul   `[{ categoryId, frazy: [...] }]` z `/api/categories/frazy-wyszukiwania`;
 *                     pusta lista jest w porządku — wtedy działa punkt 1–3.
 */
export function zbudujIndeksOkien(kategorie, frazyRegul) {
    const lista = Array.isArray(kategorie) ? kategorie : [];
    const poId = new Map(lista.map((c) => [klucz(c.id), c]));
    const siano = new Map();

    const dopisz = (idOkna, tekst) => {
        const t = normalizuj(tekst);
        if (!t) return;
        const dotychczas = siano.get(idOkna);
        siano.set(idOkna, dotychczas ? `${dotychczas} | ${t}` : t);
    };

    lista.filter(jestOknem).forEach((okno) => {
        const id = klucz(okno.id);
        siano.set(id, "");
        dopisz(id, okno.name);
        dopisz(id, okno.tileDescription);
    });

    /* Kategorie pod oknem. Idziemy w GÓRĘ od każdej kategorii, a nie w dół od okna, bo drzewo bywa
       głębsze niż jeden poziom (grupa → okno → kategoria) i szukanie w dół gubiłoby wnuki. */
    lista.filter((c) => !jestOknem(c) && !jestGrupa(c)).forEach((c) => {
        const okno = przodekSpelniajacy(c, poId, jestOknem);
        if (okno) dopisz(klucz(okno.id), c.name);
    });

    (Array.isArray(frazyRegul) ? frazyRegul : []).forEach((wpis) => {
        const cel = poId.get(klucz(wpis && wpis.categoryId));
        if (!cel) return;
        /* Reguła wskazuje KATEGORIĘ — ta bywa oknem albo kategorią pod oknem. Frazę przypinamy do
           okna, bo to okno jest tym, co człowiek klika. */
        const okno = przodekSpelniajacy(cel, poId, jestOknem);
        if (!okno) return;
        (Array.isArray(wpis.frazy) ? wpis.frazy : []).forEach((f) => dopisz(klucz(okno.id), f));
    });

    return siano;
}

/**
 * Zawężenie listy okien frazą. Pusta fraza = pełna lista (a nie pusty wynik!).
 *
 * Reguła dopasowania: KAŻDE słowo zapytania musi wystąpić w sianie okna. „nie drukuje" trafia
 * w okno, którego słownik zna frazę „nie drukuje"; „drukarka kolor" zawęzi do okna znającego oba
 * słowa. Dopasowanie po pojedynczym słowie („lub") dawało przy ~40 oknach wynik prawie pełny,
 * czyli bezużyteczny.
 */
export function filtrujOknaPoFrazie(okna, indeks, fraza) {
    const lista = Array.isArray(okna) ? okna : [];
    const slowa = normalizuj(fraza).split(" ").filter(Boolean);
    if (slowa.length === 0) return lista;
    return lista.filter((okno) => {
        const siano = (indeks && indeks.get(klucz(okno.id))) || "";
        return slowa.every((s) => siano.includes(s));
    });
}

/**
 * Układ siatki w SEKCJE. Sekcja = najbliższa grupa (`isGroup`) nad oknem; okna bez grupy lądują
 * w sekcji bez nagłówka, na końcu.
 *
 * Gdy grup nie ma w ogóle (stan produkcji 2026-09-19: 8 kategorii, zero grup), wychodzi dokładnie
 * jedna sekcja bez nagłówka — czyli siatka wygląda jak dotąd. To celowe: układ sekcyjny ma się
 * włączyć sam, kiedy administrator założy grupy, a nie wymagać drugiej decyzji.
 */
export function pogrupujOknaWSekcje(okna, kategorie) {
    const lista = Array.isArray(okna) ? okna : [];
    const poId = new Map((Array.isArray(kategorie) ? kategorie : []).map((c) => [klucz(c.id), c]));
    const sekcje = new Map();
    const bezGrupy = [];

    lista.forEach((okno) => {
        const rodzic = poId.get(klucz(okno.parentCategoryId));
        const grupa = rodzic ? przodekSpelniajacy(rodzic, poId, jestGrupa) : null;
        if (!grupa) { bezGrupy.push(okno); return; }
        const id = klucz(grupa.id);
        if (!sekcje.has(id)) sekcje.set(id, { id, nazwa: grupa.name, sortOrder: grupa.sortOrder || 0, okna: [] });
        sekcje.get(id).okna.push(okno);
    });

    const posortowane = [...sekcje.values()].sort(
        (a, b) => (a.sortOrder - b.sortOrder) || String(a.nazwa || "").localeCompare(String(b.nazwa || ""), "pl")
    );
    if (bezGrupy.length > 0) posortowane.push({ id: null, nazwa: null, sortOrder: 0, okna: bezGrupy });
    return posortowane;
}

/**
 * Lista modułów do chipów filtra — BRANA Z DANYCH, nigdy z kodu.
 *
 * Kolejność i nazwy pochodzą ze słownika `TicketModules` (aktywne moduły, posortowane po `SortOrder`
 * po stronie serwera). Zostają wyłącznie moduły, które NAPRAWDĘ mają choć jedno widoczne okno —
 * chip prowadzący do pustej siatki jest gorszy niż brak chipa. Moduł, którego okna istnieją, a
 * którego nie ma w słowniku (kod ustawiony ręcznie w kategorii), i tak dostaje chip z kodem: inaczej
 * jego okna zniknęłyby z każdego filtra i nie dałoby się ich zawęzić.
 */
export function modulyDoFiltra(okna, modulyZBazy) {
    const kodyOkien = [];
    (Array.isArray(okna) ? okna : []).forEach((c) => {
        const kod = c && c.module ? String(c.module) : "";
        if (kod && !kodyOkien.includes(kod)) kodyOkien.push(kod);
    });

    const wynik = [];
    (Array.isArray(modulyZBazy) ? modulyZBazy : []).forEach((m) => {
        const kod = m && m.code ? String(m.code) : "";
        if (!kod || !kodyOkien.includes(kod)) return;
        if (wynik.some((w) => w.kod === kod)) return;
        wynik.push({ kod, nazwa: m.name || kod });
    });
    kodyOkien.forEach((kod) => {
        if (!wynik.some((w) => w.kod === kod)) wynik.push({ kod, nazwa: kod });
    });
    return wynik;
}
